import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiClient, type components } from '@/api/client';
import { patientQueries } from '@/features/patient-record/queries';
import { mapNote } from '@/features/patient-record/mappers/note';
import type { NoteType, NoteViewModel } from '@/features/patient-record/types';

type NoteInputDto = components['schemas']['NoteInput'];

export interface AddNoteInput {
  readonly noteType: NoteType;
  readonly subject: string | null;
  readonly body: string;
  /** GUID of the user creating the note (real auth lands in PTV2-910). */
  readonly userId: string;
}

export interface UpdateNoteInput {
  readonly id: string;
  readonly noteType: NoteType;
  readonly subject: string | null;
  readonly body: string;
  /** GUID of the user editing the note. BE uses session principal; echoed for fixtures/MSW. */
  readonly userId: string;
}

interface AddMutationContext {
  readonly previous: NoteViewModel[] | undefined;
}

interface UpdateMutationContext {
  readonly previous: NoteViewModel[] | undefined;
}

interface RemoveMutationContext {
  readonly previous: NoteViewModel[] | undefined;
}

type AddResult = UseMutationResult<NoteViewModel, Error, AddNoteInput, AddMutationContext>;
type UpdateResult = UseMutationResult<
  NoteViewModel,
  Error,
  UpdateNoteInput,
  UpdateMutationContext
>;
type RemoveResult = UseMutationResult<undefined, Error, string, RemoveMutationContext>;

/**
 * Resource-level mutations for patient notes. `add` appends a new note;
 * `update` edits the author's own note within the edit window; `remove`
 * soft-deletes the author's own note within the edit window (BE keeps the
 * row for audit, FE drops it from the visible list).
 *
 * All three follow the patient-record mutation pattern:
 *
 *   1. ``onMutate`` cancels in-flight queries and writes an optimistic view
 *      model into the cache.
 *   2. ``onError`` restores the snapshot captured before mutating — never
 *      deletes the optimistic entry directly, because the cache may have
 *      been refetched in between.
 *   3. ``onSettled`` invalidates the canonical key so the next render
 *      reconciles with the server.
 */
export function useNoteMutations(guid: string): {
  readonly add: AddResult;
  readonly update: UpdateResult;
  readonly remove: RemoveResult;
} {
  const queryClient = useQueryClient();
  const { queryKey } = patientQueries.notes(guid);

  const add = useMutation<NoteViewModel, Error, AddNoteInput, AddMutationContext>({
    meta: { successMessage: 'success.toast.title.note_created' },
    mutationFn: async (input) => {
      const body: NoteInputDto = {
        note_type: input.noteType,
        subject: input.subject,
        body: input.body,
        user_id: input.userId,
      };
      const dto = await apiClient.addNote(guid, body);
      return mapNote(dto);
    },

    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);

      const optimistic: NoteViewModel = {
        id: `optimistic-${Date.now().toString(36)}`,
        noteType: input.noteType,
        subject: input.subject,
        body: input.body,
        authorName: null,
        authorAccountId: input.userId,
        createdAt: new Date().toISOString(),
        modifiedOn: null,
      };
      queryClient.setQueryData(queryKey, [optimistic, ...(previous ?? [])]);
      return { previous };
    },

    onError: (_error, _input, context) => {
      if (context === undefined) return;
      queryClient.setQueryData(queryKey, context.previous);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
    },
  });

  const update = useMutation<NoteViewModel, Error, UpdateNoteInput, UpdateMutationContext>({
    meta: { successMessage: 'success.toast.title.note_updated' },
    mutationFn: async (input) => {
      const body: NoteInputDto = {
        note_type: input.noteType,
        subject: input.subject,
        body: input.body,
        user_id: input.userId,
      };
      const dto = await apiClient.updateNote(guid, input.id, body);
      return mapNote(dto);
    },

    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);

      const next = (previous ?? []).map((note) =>
        note.id === input.id
          ? {
              ...note,
              noteType: input.noteType,
              subject: input.subject,
              body: input.body,
              modifiedOn: new Date().toISOString(),
            }
          : note,
      );
      queryClient.setQueryData(queryKey, next);
      return { previous };
    },

    onError: (_error, _input, context) => {
      if (context === undefined) return;
      queryClient.setQueryData(queryKey, context.previous);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
    },
  });

  const remove = useMutation<undefined, Error, string, RemoveMutationContext>({
    meta: { successMessage: 'success.toast.title.note_deleted' },
    mutationFn: async (id) => {
      await apiClient.deleteNote(guid, id);
      return undefined;
    },

    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(
        queryKey,
        (previous ?? []).filter((note) => note.id !== id),
      );
      return { previous };
    },

    onError: (_error, _id, context) => {
      if (context === undefined) return;
      queryClient.setQueryData(queryKey, context.previous);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
    },
  });

  return { add, update, remove };
}
